// =====================================================
// src/pages/MijnActiesignalen.js
// vSIG-015
// - Rubrieken volgen nu StaffIQ-configuratie uit Instellingen (CRM / MKB / HRM / ALL).
// - CRM, Personeel, Documenten en Documentsets blijven algemeen zichtbaar.
// - MKB toont aanvullend Offertes, Opdrachten, Werkbonnen en Facturen.
// - HRM toont aanvullend Aanvragen, Vacatures en Plaatsingen.
//
// Wijziging vSIG-013:
// - Rubriek Werkbonnen nu actief voor de vier automatische werkbonsignalen.
// - Bestaande groepering en instellingenlogica ongewijzigd.
//
// Wijziging vSIG-012:
// - Vaste, toekomstvaste volgorde van de rubrieken voorbereid.
// - Huidige volgorde: CRM, Personeel, Plaatsingen, Documenten, Documentsets.
// - Voor MKB voorbereid: Offertes, Opdrachten, Werkbonnen, Facturen.
// - Voor HRM voorbereid: Aanvragen, Vacatures, Plaatsingen.
// - Alleen rubrieken waarvoor signalen bestaan worden getoond.
// - Functionele werking van de signaalinstellingen verder ongewijzigd.
// =====================================================

import React, { useEffect, useMemo, useState } from "react";
import HelpButton from "../components/HelpButton";

const VERSION = "vSIG-015";

// =====================================================
// SECTION 1: API HEADERS
// =====================================================
function headers() {
  let user = {};

  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  return {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    ...(user.id ? { "x-staffiq-user-id": String(user.id) } : {}),
    ...(user.email ? { "x-staffiq-user-email": user.email } : {}),
    ...(user.role ? { "x-role": user.role } : {}),
  };
}

// =====================================================
// SECTION 2: STANDAARD REGELMODEL
// =====================================================
const blank = (definition) => ({
  signal_definition_id: definition.id,
  enabled: false,
  days_value: definition.default_days ?? 0,
  urgency: definition.default_urgency || "info",
  show_in_action_center: true,
  email_enabled: false,
});

// =====================================================
// SECTION 3: HOOFDCOMPONENT
// =====================================================
export default function MijnActiesignalen() {
  const [defs, setDefs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});

  const [scope, setScope] = useState("defaults");
  const [code, setCode] = useState("");
  const [staffiqProfile, setStaffiqProfile] = useState("ALL");

  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");

  // ---------------------------------------------------
  // Data laden
  // ---------------------------------------------------
  async function load(showFeedback = false) {
    setErr("");
    setFlash("");

    try {
      const [response, profileResponse] = await Promise.all([
        fetch("/api/action-center/signal-settings", { headers: headers() }),
        fetch("/api/general-settings", { headers: headers() }),
      ]);

      const data = await response.json();
      const profileData = await profileResponse.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Laden mislukt.");
      }

      setStaffiqProfile(
        profileResponse.ok
          ? String(profileData?.staffiq_profile || "ALL").toUpperCase()
          : "ALL"
      );

      setDefs(data.definitions || []);
      setCodes(data.company_codes || []);

      if (!code && data.company_codes?.length) {
        setCode(String(data.company_codes[0].id));
      }

      const defaultRows = {};

      (data.definitions || []).forEach((definition) => {
        const saved = (data.defaults || []).find(
          (row) =>
            String(row.signal_definition_id) === String(definition.id)
        );

        defaultRows[definition.id] = saved
          ? { ...blank(definition), ...saved }
          : blank(definition);
      });

      setDefaults(defaultRows);

      const overrideRows = {};

      (data.overrides || []).forEach((row) => {
        overrideRows[row.company_code_id] ??= {};
        overrideRows[row.company_code_id][row.signal_definition_id] = row;
      });

      setOverrides(overrideRows);

      if (showFeedback) {
        setFlash("Vernieuwd ✅");
        setTimeout(() => setFlash(""), 800);
      }
    } catch (error) {
      setErr(error.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------
  // Zichtbare regels samenstellen
  // ---------------------------------------------------
  const rows = useMemo(
    () =>
      defs.map((definition) => {
        const base = defaults[definition.id] || blank(definition);
        const override = (overrides[code] || {})[definition.id];

        return {
          definition,
          value:
            scope === "defaults"
              ? base
              : { ...base, ...(override || {}) },
          inherited: scope !== "defaults" && !override,
        };
      }),
    [defs, defaults, overrides, scope, code]
  );

  // ---------------------------------------------------
  // Eén veld wijzigen
  // ---------------------------------------------------
  function change(id, key, value) {
    if (scope === "defaults") {
      setDefaults((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [key]: value,
        },
      }));

      return;
    }

    setOverrides((prev) => {
      const companyRows = { ...(prev[code] || {}) };
      const definition = defs.find((item) => item.id === id) || {};
      const base = defaults[id] || blank(definition);

      companyRows[id] = {
        ...base,
        ...(companyRows[id] || {}),
        signal_definition_id: id,
        [key]: value,
      };

      return {
        ...prev,
        [code]: companyRows,
      };
    });
  }

  // ---------------------------------------------------
  // Opslaan
  // Zelfde korte melding als de overige StaffIQ-schermen.
  // ---------------------------------------------------
  async function save() {
    setErr("");
    setFlash("");

    try {
      const body =
        scope === "defaults"
          ? {
              scope: "defaults",
              rows: defs.map(
                (definition) =>
                  defaults[definition.id] || blank(definition)
              ),
            }
          : {
              scope: "company",
              company_code_id: Number(code),
              rows: defs.map((definition) => ({
                ...defaults[definition.id],
                ...((overrides[code] || {})[definition.id] || {}),
              })),
            };

      const response = await fetch(
        "/api/action-center/signal-settings",
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Opslaan mislukt.");
      }

      await load();

      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);
    } catch (error) {
      setErr(error.message);
    }
  }

  // ---------------------------------------------------
  // Afwijkingen van één bedrijfscode verwijderen
  // ---------------------------------------------------
  async function reset() {
    if (
      !window.confirm(
        "Afwijkingen voor deze bedrijfscode verwijderen?"
      )
    ) {
      return;
    }

    setErr("");
    setFlash("");

    try {
      const response = await fetch(
        `/api/action-center/signal-settings/${code}`,
        {
          method: "DELETE",
          headers: headers(),
          body: "{}",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Herstellen mislukt.");
      }

      await load();

      setFlash("Standaardinstellingen hersteld ✅");
      setTimeout(() => setFlash(""), 800);
    } catch (error) {
      setErr(error.message);
    }
  }

  function sourceGroup(definition = {}) {
    const source = String(definition.source_type || "").toLowerCase();
    const module = String(definition.module || "").toLowerCase();

    // Algemene CRM-bronnen
    if (
      source === "organisation" ||
      source === "organization" ||
      source === "relation" ||
      module === "crm"
    ) return "CRM";

    // Personeel
    if (
      source === "personnel" ||
      source === "employee" ||
      module === "personeel"
    ) return "Personeel";

    // MKB - alvast voorbereid voor volgende fasen
    if (
      source === "offer" ||
      source === "offerte" ||
      module === "offertes"
    ) return "Offertes";

    if (
      source === "order" ||
      source === "opdracht" ||
      module === "opdrachten"
    ) return "Opdrachten";

    if (
      source === "work_order" ||
      source === "werkbon" ||
      module === "werkbonnen"
    ) return "Werkbonnen";

    if (
      source === "invoice" ||
      source === "factuur" ||
      module === "facturen"
    ) return "Facturen";

    // HRM - alvast voorbereid voor volgende fasen
    if (
      source === "application" ||
      source === "aanvraag" ||
      module === "aanvragen"
    ) return "Aanvragen";

    if (
      source === "vacancy" ||
      source === "vacature" ||
      module === "vacatures"
    ) return "Vacatures";

    if (
      source === "placement" ||
      source === "plaatsing" ||
      module === "plaatsingen"
    ) return "Plaatsingen";

    // Documenten altijd onderaan, onafhankelijk van CRM/HRM/MKB-module
    if (source === "document") return "Documenten";
    if (source === "document_set") return "Documentsets";

    return definition.module || "Overig";
  }

  const groupedRows = useMemo(() => {
    const map = new Map();

    const profile = String(staffiqProfile || "ALL").toUpperCase();

    const alwaysVisible = new Set([
      "CRM",
      "Personeel",
      "Documenten",
      "Documentsets",
    ]);

    const mkbVisible = new Set([
      "Offertes",
      "Opdrachten",
      "Werkbonnen",
      "Facturen",
    ]);

    const hrmVisible = new Set([
      "Aanvragen",
      "Vacatures",
      "Plaatsingen",
    ]);

    function groupAllowed(group) {
      if (profile === "ALL" || profile === "ERP") return true;
      if (alwaysVisible.has(group)) return true;
      if (profile === "MKB" && mkbVisible.has(group)) return true;
      if (profile === "HRM" && hrmVisible.has(group)) return true;
      return false;
    }

    rows.forEach((row) => {
      const group = sourceGroup(row.definition);
      if (!groupAllowed(group)) return;
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(row);
    });

    // Vaste StaffIQ-volgorde.
    // Rubrieken zonder signalen worden automatisch niet getoond.
    const preferred = [
      "CRM",
      "Personeel",

      // MKB
      "Offertes",
      "Opdrachten",
      "Werkbonnen",
      "Facturen",

      // HRM
      "Aanvragen",
      "Vacatures",
      "Plaatsingen",

      // Algemeen
      "Documenten",
      "Documentsets",
    ];

    return Array.from(map.entries()).sort((a, b) => {
      const ai = preferred.indexOf(a[0]);
      const bi = preferred.indexOf(b[0]);

      if (ai >= 0 || bi >= 0) {
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      }

      return a[0].localeCompare(b[0], "nl");
    });
  }, [rows, staffiqProfile]);

  // =====================================================
  // SECTION 4: STYLING
  // =====================================================
  const inputStyle = {
    width: "100%",
    padding: "6px",
    border: "1px solid #ccc",
    borderRadius: 5,
    boxSizing: "border-box",
    fontSize: 13,
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const saveButtonStyle = {
    ...buttonStyle,
    background: "#1976d2",
    color: "white",
    border: "1px solid #1976d2",
  };

  // =====================================================
  // SECTION 5: RENDER
  // =====================================================
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <h1 style={{ margin: 0 }}>
          Mijn Actiesignalen{" "}
          <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
        </h1>

        <HelpButton
          topic="mijn-actiesignalen"
          title="Help over Mijn Actiesignalen"
        />
      </div>

      {/* StaffIQ-standaard meldingen */}
      <div>
        {flash && (
          <div
            style={{
              marginBottom: 8,
              color: "green",
            }}
          >
            {flash}
          </div>
        )}

        {err && (
          <div
            style={{
              marginBottom: 8,
              color: "crimson",
            }}
          >
            {err}
          </div>
        )}
      </div>

      <div
        style={{
          background: "white",
          padding: 14,
          border: "1px solid #ddd",
          borderRadius: 8,
          marginTop: 4,
          display: "flex",
          gap: 10,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 280 }}>
          <b>Instellingen voor</b>

          <select
            style={inputStyle}
            value={scope === "defaults" ? "defaults" : code}
            onChange={(event) => {
              if (event.target.value === "defaults") {
                setScope("defaults");
              } else {
                setScope("company");
                setCode(event.target.value);
              }
            }}
          >
            <option value="defaults">
              Standaard voor mijn bedrijfscodes
            </option>

            {codes.map((companyCode) => (
              <option
                key={companyCode.id}
                value={companyCode.id}
              >
                {companyCode.name || companyCode.code}
              </option>
            ))}
          </select>
        </div>

        <button onClick={() => load(true)} style={buttonStyle}>
          🔄 Vernieuw
        </button>

        {scope === "company" && (
          <button onClick={reset} style={buttonStyle}>
            ↩ Gebruik standaard
          </button>
        )}

        <button
          onClick={save}
          style={saveButtonStyle}
        >
          💾 Opslaan
        </button>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#5d6b78", lineHeight: 1.5 }}>
        <b>E-mail dagoverzicht:</b> aangevinkte signalen worden gebundeld in één dagelijkse e-mail met jouw openstaande acties.
        Als niets voor e-mail is aangevinkt of er zijn geen bijbehorende open acties, wordt geen e-mail verstuurd.
      </div>

      <div
        style={{
          overflowX: "auto",
          background: "white",
          marginTop: 12,
          border: "1px solid #ddd",
          borderRadius: 8,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: 900,
          }}
        >
          <thead>
            <tr style={{ background: "#f4f6f8" }}>
              {[
                "Actie / signaal",
                "Aan",
                "Dagen",
                "Urgentie",
                "Actiecentrum",
                "E-mail dagoverzicht",
                "Status",
              ].map((heading) => (
                <th
                  key={heading}
                  style={{
                    padding: 8,
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groupedRows.map(([group, groupRows]) => (
              <React.Fragment key={group}>
                <tr><td colSpan={7} style={{padding:"9px 8px",background:"#eef3f7",borderTop:"1px solid #d7e0e7",borderBottom:"1px solid #d7e0e7",fontWeight:700,color:"#1f4e79"}}>{group}</td></tr>
                {groupRows.map(({ definition, value, inherited }) => (
                <tr key={definition.id}>
                  <td
                    style={{
                      padding: 8,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <b>{definition.label}</b>

                    <div
                      style={{
                        fontSize: 12,
                        color: "#667085",
                      }}
                    >
                      {definition.description}
                    </div>
                  </td>

                  <td align="center">
                    <input
                      type="checkbox"
                      checked={value.enabled === true}
                      onChange={(event) =>
                        change(
                          definition.id,
                          "enabled",
                          event.target.checked
                        )
                      }
                    />
                  </td>

                  <td
                    style={{
                      padding: 5,
                      width: 100,
                    }}
                  >
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      value={value.days_value ?? 0}
                      disabled={definition.source_type === "document_set"}
                      onChange={(event) =>
                        change(
                          definition.id,
                          "days_value",
                          Number(event.target.value || 0)
                        )
                      }
                    />
                  </td>

                  <td
                    style={{
                      padding: 5,
                      width: 120,
                    }}
                  >
                    <select
                      style={inputStyle}
                      value={value.urgency || "info"}
                      onChange={(event) =>
                        change(
                          definition.id,
                          "urgency",
                          event.target.value
                        )
                      }
                    >
                      <option value="info">Info</option>
                      <option value="normaal">Normaal</option>
                      <option value="hoog">Hoog</option>
                      <option value="spoed">Spoed</option>
                    </select>
                  </td>

                  <td align="center">
                    <input
                      type="checkbox"
                      checked={
                        value.show_in_action_center !== false
                      }
                      onChange={(event) =>
                        change(
                          definition.id,
                          "show_in_action_center",
                          event.target.checked
                        )
                      }
                    />
                  </td>

                  <td align="center">
                    <input
                      type="checkbox"
                      checked={value.email_enabled === true}
                      onChange={(event) =>
                        change(
                          definition.id,
                          "email_enabled",
                          event.target.checked
                        )
                      }
                    />
                  </td>

                  <td
                    align="center"
                    style={{
                      fontSize: 12,
                      color: "#667085",
                    }}
                  >
                    {scope === "company"
                      ? inherited
                        ? "Standaard"
                        : "Afwijkend"
                      : ""}
                  </td>
                </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, paddingBottom: 12 }}>
        <button type="button" onClick={save} style={saveButtonStyle}>
          💾 Opslaan
        </button>
      </div>
    </div>
  );
}
